import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, RotateCcw, PlusCircle, Trash2, FileSpreadsheet, FileText, Loader2, Upload, FileDown } from "lucide-react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import OrdersTable from "@/components/OrdersTable";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["Picked Up", "In Transit", "Delivered", "Returned to Client"];
const TYPES = ["Normal", "Express"];

export default function OrdersPage() {
  const { user } = useAuth();
  const isClient = user?.role === "client";
  const isAdmin = user?.role === "admin";
  const [params] = useSearchParams();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [exporting, setExporting] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/orders/template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ezship-orders-template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submitUpload = async () => {
    if (!uploadFile) {
      toast.error("Choose an .xlsx file first (download the sample for the format).");
      return;
    }
    const fd = new FormData();
    fd.append("file", uploadFile);
    setUploading(true);
    setUploadResult(null);
    try {
      const { data } = await api.post("/orders/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadResult(data);
      if (data.created > 0) {
        toast.success(`${data.created} order(s) created from Excel.`);
        fetchOrders();
      }
      if (data.failed.length > 0) toast.error(`${data.failed.length} row(s) failed — see details.`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  const exportFile = async (fmt) => {
    setExporting(fmt);
    try {
      const p = new URLSearchParams();
      if (search.trim()) p.set("search", search.trim());
      if (!isClient && clientId !== "all") p.set("client_id", clientId);
      if (status !== "all") p.set("status", status);
      if (dtype !== "all") p.set("delivery_type", dtype);
      if (isAdmin && staffId !== "all") p.set("staff_id", staffId);
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
      const res = await api.get(`/orders/export.${fmt}?${p.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ezship-orders.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Orders exported as ${fmt === "xlsx" ? "Excel" : "PDF"}.`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setExporting("");
    }
  };

  const [search, setSearch] = useState("");
  const [clientId, setClientId] = useState(params.get("client_id") || "all");
  const [status, setStatus] = useState("all");
  const [dtype, setDtype] = useState("all");
  const [staffId, setStaffId] = useState(params.get("staff_id") || "all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const p = new URLSearchParams();
      if (search.trim()) p.set("search", search.trim());
      if (!isClient && clientId !== "all") p.set("client_id", clientId);
      if (status !== "all") p.set("status", status);
      if (dtype !== "all") p.set("delivery_type", dtype);
      if (isAdmin && staffId !== "all") p.set("staff_id", staffId);
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
      const { data } = await api.get(`/orders?${p.toString()}`);
      setOrders(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [search, clientId, status, dtype, staffId, dateFrom, dateTo, isClient, isAdmin]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  useEffect(() => {
    if (!isClient) api.get("/clients/options").then((r) => setClients(r.data)).catch(() => {});
    if (isAdmin) api.get("/staff").then((r) => setStaffList(r.data)).catch(() => {});
  }, [isClient, isAdmin]);

  const clearFilters = () => {
    setSearch(""); setClientId("all"); setStatus("all"); setDtype("all");
    setStaffId("all"); setDateFrom(""); setDateTo("");
  };

  const toggleSelect = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });

  const toggleSelectAll = (ids) =>
    setSelected((s) => {
      const n = new Set(s);
      const allIn = ids.every((id) => n.has(id));
      ids.forEach((id) => (allIn ? n.delete(id) : n.add(id)));
      return n;
    });

  const quickTransit = async (order) => {
    try {
      await api.put(`/orders/${order.id}`, { delivery_status: "In Transit" });
      toast.success(`${order.order_id} marked Out for Delivery (In Transit).`);
      fetchOrders();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const confirmBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/orders/bulk-delete", { order_ids: [...selected] });
      toast.success(`${data.deleted} order(s) deleted.`);
      setSelected(new Set());
      setBulkDeleteOpen(false);
      fetchOrders();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBulkDeleting(false);
    }
    setBulkDeleting(false);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/orders/${deleteTarget.id}`);
      toast.success(`Order ${deleteTarget.order_id} deleted.`);
      setDeleteTarget(null);
      fetchOrders();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const title = isClient ? "My Orders" : isAdmin ? "All Orders" : "Orders";

  return (
    <div className="space-y-5" data-testid="orders-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="orders-title">{title}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {isClient ? "All orders generated for your account." : "Search, filter and manage orders."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <>
              <button
                onClick={downloadTemplate}
                data-testid="download-template-button"
                className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-3 py-2.5 rounded-sm transition-colors duration-150"
              >
                <FileDown className="h-4 w-4 text-primary" /> Sample File
              </button>
              <button
                onClick={() => { setUploadOpen(true); setUploadResult(null); setUploadFile(null); }}
                data-testid="upload-excel-button"
                className="inline-flex items-center gap-2 border border-primary text-primary hover:bg-primary/5 text-sm font-semibold px-3 py-2.5 rounded-sm transition-colors duration-150"
              >
                <Upload className="h-4 w-4" /> Upload Excel
              </button>
            </>
          )}
          <button
            onClick={() => exportFile("xlsx")}
            disabled={Boolean(exporting)}
            data-testid="export-excel-button"
            className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-3 py-2.5 rounded-sm transition-colors duration-150 disabled:opacity-50"
          >
            {exporting === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-700" />} Excel
          </button>
          <button
            onClick={() => exportFile("pdf")}
            disabled={Boolean(exporting)}
            data-testid="export-pdf-button"
            className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-3 py-2.5 rounded-sm transition-colors duration-150 disabled:opacity-50"
          >
            {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-[#E63946]" />} PDF
          </button>
          {!isClient && (
            <Link to="/orders/new" data-testid="orders-create-button"
              className="inline-flex items-center gap-2 bg-primary hover:bg-[#152D53] text-white text-sm font-semibold px-4 py-2.5 rounded-sm transition-colors duration-150">
              <PlusCircle className="h-4 w-4" /> Create Order
            </Link>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="orders-filters">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3">
          <div className="relative sm:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="orders-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search order ID, passenger, phone, ref no..."
              className="pl-9 h-10 border-slate-300 rounded-sm"
            />
          </div>
          {!isClient && (
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger data-testid="filter-client" className="h-10 border-slate-300 rounded-sm"><SelectValue placeholder="Client" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="filter-status" className="h-10 border-slate-300 rounded-sm"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={dtype} onValueChange={setDtype}>
            <SelectTrigger data-testid="filter-type" className="h-10 border-slate-300 rounded-sm"><SelectValue placeholder="Delivery Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          {isAdmin && (
            <Select value={staffId} onValueChange={setStaffId}>
              <SelectTrigger data-testid="filter-staff" className="h-10 border-slate-300 rounded-sm"><SelectValue placeholder="Created By" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Staff</SelectItem>
                {staffList.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">From date</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" className="h-10 border-slate-300 rounded-sm" placeholder="From date" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">To date</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" className="h-10 border-slate-300 rounded-sm" placeholder="To date" />
          </div>
          <button
            onClick={clearFilters}
            data-testid="clear-filters-button"
            className="inline-flex items-center justify-center gap-2 h-10 px-4 border border-slate-300 rounded-sm text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors duration-150"
          >
            <RotateCcw className="h-4 w-4" /> Clear Filters
          </button>
        </div>
      </div>

      {isAdmin && (
        <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
          <DialogContent data-testid="bulk-upload-dialog">
            <DialogHeader>
              <DialogTitle>Upload Orders from Excel</DialogTitle>
              <DialogDescription>
                Upload an .xlsx file using the sample format. Client names must match existing active clients. Leave "Order ID" blank to auto-generate, or enter your own custom order numbers.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <input
                type="file"
                accept=".xlsx"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                data-testid="bulk-file-input"
                className="text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-sm file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-[#152D53] file:cursor-pointer"
              />
              <button onClick={downloadTemplate} data-testid="dialog-download-template" className="text-xs font-semibold text-primary hover:underline">
                Download sample file with example data
              </button>
              {uploadResult && (
                <div className="border border-slate-200 rounded-sm p-3 space-y-1" data-testid="upload-result">
                  <div className="text-sm font-semibold text-green-700">Created: {uploadResult.created} order(s)</div>
                  {uploadResult.failed.length > 0 && (
                    <div className="text-xs text-red-600 space-y-0.5 max-h-32 overflow-y-auto" data-testid="upload-failures">
                      {uploadResult.failed.map((f, i) => (
                        <div key={i}>Row {f.row}: {f.reason}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadOpen(false)} data-testid="bulk-upload-cancel" className="rounded-sm">Close</Button>
              <Button onClick={submitUpload} disabled={uploading} data-testid="bulk-upload-submit" className="bg-primary hover:bg-[#152D53] rounded-sm">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upload & Create Orders"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {isAdmin && selected.size > 0 && (
        <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-md px-4 py-3 flex-wrap" data-testid="orders-bulk-bar">
          <span className="text-xs font-semibold text-slate-700 tnum">{selected.size} selected</span>
          <button
            onClick={() => setBulkDeleteOpen(true)}
            data-testid="bulk-delete-button"
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-sm text-xs font-semibold bg-[#E63946] hover:bg-[#C8323D] text-white transition-colors duration-150"
          >
            <Trash2 className="h-3.5 w-3.5" /> Delete Selected
          </button>
          <button onClick={() => setSelected(new Set())} data-testid="bulk-clear-selection" className="text-xs font-medium text-slate-500 hover:text-slate-800 transition-colors duration-150">
            Clear selection
          </button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md">
        <OrdersTable
          orders={orders}
          loading={loading}
          showClient={!isClient}
          showCreator={!isClient}
          canEdit={!isClient}
          onDelete={isAdmin ? setDeleteTarget : null}
          selectable={isAdmin}
          selected={selected}
          onToggleSelect={toggleSelect}
          onToggleSelectAll={toggleSelectAll}
          onQuickTransit={quickTransit}
        />
      </div>

      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title={`Delete ${selected.size} order(s)?`}
        description="The selected orders will be permanently deleted. This action is recorded in the audit log."
        confirmLabel={bulkDeleting ? "Deleting..." : "Delete Orders"}
        destructive
        onConfirm={confirmBulkDelete}
        testId="bulk-delete-dialog"
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete this order?"
        description={`Order ${deleteTarget?.order_id} (${deleteTarget?.passenger_name}) will be permanently deleted. This action is recorded in the audit log.`}
        confirmLabel={deleting ? "Deleting..." : "Delete Order"}
        destructive
        onConfirm={confirmDelete}
        testId="delete-order-dialog"
      />
    </div>
  );
}
